/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { Collapse, ListItem, ListItemIcon, ListItemText } from '@mui/material';

import { OrganizationRole } from 'shared';

import { Namespaces } from '../../../../../i18n';
import { useDetailsPanelInfoStyles } from '../../../../../styles/detailsPanelInfo';
import { AdminInstitutionUserDetails } from '../../../../../types/superuser/AdminInstitutionUserDetails';

type UsersInfoListProps = {
  userDetails: AdminInstitutionUserDetails;
  role?: OrganizationRole;
};

export function UsersInfoList(props: UsersInfoListProps) {
  const { userDetails } = props;
  const { t } = useTranslation(Namespaces.Admin);
  const classes = useDetailsPanelInfoStyles();
  const [openUser, setOpenUser] = useState<boolean>(false);

  const onClick = () => setOpenUser(!openUser);

  const phoneNumber = useMemo<string>(() => {
    if (!userDetails.phoneNationalNumber && !userDetails.phoneCountryCode) {
      return '';
    }

    return `+${t(`countries:${userDetails.phoneCountryCode}.callingCode`)} ${userDetails.phoneNationalNumber}`;
  }, [t, userDetails.phoneCountryCode, userDetails.phoneNationalNumber]);

  return (
    <>
      <ListItem button onClick={onClick}>
        <ListItemIcon>
          <AccountCircleIcon />
        </ListItemIcon>
        <ListItemText
          primary={`${userDetails.firstName}
          ${userDetails.middleName || ''} 
            ${userDetails.lastName}`}
          secondary={props.role ?? ''}
        />
      </ListItem>
      <Collapse in={openUser} timeout="auto" unmountOnExit>
        <div className={classes.root}>
          <div className={classes.row}>
            <div className={classes.column}>
              <div className={classes.linesContainer}>
                <div className={classes.line}>
                  <b>{t('organizations.info_list.user.first_name')}</b>:<span>{userDetails.firstName}</span>
                </div>
              </div>
              <div className={classes.linesContainer}>
                <div className={classes.line}>
                  <b>{t('organizations.info_list.user.middle_name')}</b>:
                  <span>{userDetails.middleName ? userDetails.middleName : '-'}</span>
                </div>
              </div>
              <div className={classes.linesContainer}>
                <div className={classes.line}>
                  <b>{t('organizations.info_list.user.last_name')}</b>:<span>{userDetails.lastName}</span>
                </div>
              </div>
              <div className={classes.linesContainer}>
                <div className={classes.line}>
                  <b>{t('organizations.info_list.user.email_address')}</b>:<span>{userDetails.email}</span>
                </div>
              </div>
              {props.role && (
                <div className={classes.linesContainer}>
                  <div className={classes.line}>
                    <b>{t('organizations.info_list.user.role')}</b>:<span>{props.role}</span>
                  </div>
                </div>
              )}
              <div className={classes.linesContainer}>
                <div className={classes.line}>
                  <b>{t('organizations.info_list.user.verified')}</b>:
                  <span>
                    {userDetails.verified
                      ? t('organizations.description_panel.yes')
                      : t('organizations.description_panel.no')}
                  </span>
                </div>
              </div>
              <div className={classes.linesContainer}>
                <div className={classes.line}>
                  <b>{t('organizations.info_list.user.marketable')}</b>:
                  <span>
                    {userDetails.marketable
                      ? t('organizations.description_panel.yes')
                      : t('organizations.description_panel.no')}
                  </span>
                </div>
              </div>
              <div className={classes.linesContainer}>
                <div className={classes.line}>
                  <b>{t('organizations.info_list.user.phone_number')}</b>:<span>{phoneNumber || '-'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Collapse>
    </>
  );
}
